import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getCurrentEvent } from "core-integration/src/store/events/reducers";
import {
  getActiveEntryInLeague,
  getH2HLeague,
  getH2HMatches,
  getH2HNewEntries,
  getH2HStandings,
  getLeagueEntries,
  getLeagueEntriesError,
} from "core-integration/src/store/leagues/reducers";
import {
  fetchH2HLeagueMatches,
  fetchH2HLeagueStandings,
  fetchLeagueEntries,
} from "core-integration/src/store/leagues/thunks";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import qs from "qs";
import React, { useCallback, useEffect, useMemo } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import Alert from "../Alert";
import Box from "../Box";
import ButtonLink from "../ButtonLink";
import { Main, Secondary, Wrapper } from "../Layout";
import { Pager, PagerItem, PagerItemNext } from "../Pager";
import H2HEntryFilter from "./H2HEntryFilter";
import H2HEventFilter from "./H2HEventFilter";
import H2HNav from "./H2HNav";
import MatchesTable from "./MatchesTable";
import StandingsHeading from "./StandingsHeading";
import { SelectWrap } from "./Styles";

type Props = WithTranslation;

const MatchesH2H: React.FC<Props> = ({ t }) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const location = useLocation();
  const navigate = useNavigate();
  const { leagueId } = useParams();

  // Get league id from path
  const leagueNumber = parseInt(leagueId!, 10) || 0;
  // Get entry id from query string
  const query = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });

  const defaultEntryNumber = parseInt(query.default_entry as string, 10) || 0;
  const entryNumber = parseInt(query.entry as string, 10) || 0;
  const now = useSelector(getCurrentEvent);
  const eventNumber = query.event
    ? parseInt(query.event as string, 10)
    : now?.id || 0;
  const pageMatches = parseInt(query.page_matches as string, 10) || 1;
  const isPlayerInLeague = Boolean(
    useSelector((state: RootState) =>
      getActiveEntryInLeague(state, leagueNumber, "h2h"),
    ),
  );
  const league = useSelector((state: RootState) =>
    getH2HLeague(state, leagueNumber),
  );
  const leagueEntries = useSelector((state: RootState) =>
    getLeagueEntries(state, leagueNumber),
  );
  const leagueEntriesError = useSelector((state: RootState) =>
    getLeagueEntriesError(state, leagueNumber),
  );
  const matches = useSelector((state: RootState) =>
    leagueNumber
      ? getH2HMatches(
          state,
          leagueNumber,
          entryNumber,
          eventNumber,
          pageMatches,
        )
      : null,
  );
  const newEntries = useSelector((state: RootState) =>
    getH2HNewEntries(state, leagueNumber, 1),
  );
  const player = useSelector(getPlayerData);
  const standings = useSelector((state: RootState) =>
    getH2HStandings(state, leagueNumber, 1),
  );

  const isLargeLeague = useMemo(() => {
    return (
      leagueEntriesError?.badRequest?.non_field_errors[0]?.code ===
      "large_leagues_forbidden"
    );
  }, [leagueEntriesError]);

  const buildMatchesUrl = useCallback(
    (
      leagueNumber: number,
      eventNumber: number,
      entryNumber: number,
      pageMatches: number,
    ) => {
      let url = `/leagues/${leagueNumber}/matches/h?page_matches=${pageMatches}`;
      if (entryNumber) url += `&entry=${entryNumber}`;
      if (now?.id && eventNumber !== now.id) url += `&event=${eventNumber}`;
      return url;
    },
    [now],
  );

  useEffect(() => {
    if (leagueNumber) {
      // Have to fetch standings to get league
      dispatch(fetchH2HLeagueStandings(leagueNumber, 1, 1));
      // We could look at delaying these calls as they are pointless for
      // unstarted h2h leagues at the cost of some complexity as we may have
      // to wait for league to be available. As a 'good enough' we don't do
      // them before the season starts ...
      if (now) {
        dispatch(fetchLeagueEntries(leagueNumber));
        dispatch(
          fetchH2HLeagueMatches(
            leagueNumber,
            entryNumber,
            eventNumber,
            pageMatches,
          ),
        );
      }
    }
  }, [leagueNumber, now, entryNumber, eventNumber, pageMatches, dispatch]);

  useEffect(() => {
    if (isLargeLeague) {
      // As isPlayerInLeague we must have a player and an entry
      const showEntry =
        defaultEntryNumber || (isPlayerInLeague ? player!.entry! : 0);
      navigate(buildMatchesUrl(leagueNumber, 0, showEntry, pageMatches), {
        replace: true,
      });
    }
  }, [
    isLargeLeague,
    leagueEntriesError,
    defaultEntryNumber,
    isPlayerInLeague,
    navigate,
    player,
    leagueNumber,
    pageMatches,
    buildMatchesUrl,
  ]);

  const handleEntryFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    navigate(
      buildMatchesUrl(
        leagueNumber,
        eventNumber,
        parseInt(e.currentTarget.value, 10),
        1,
      ),
    );

  const handleEventFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    navigate(
      buildMatchesUrl(
        leagueNumber,
        parseInt(e.currentTarget.value, 10),
        entryNumber,
        1,
      ),
    );

  if (!league || !newEntries) {
    return null;
  }
  // If we have new entries then switch to dedicated page
  if (!now || newEntries.results.length) {
    navigate(`/leagues/${league.id}/new-entries/h`, { replace: true });
  }
  if (!matches) {
    return null;
  }
  // Handle the edge case between rollover and matches being created ...
  if (
    now &&
    now.id === league.start_event &&
    standings &&
    !standings.results.length
  ) {
    return (
      <Wrapper>
        <Main>
          <StandingsHeading
            type="h2h"
            league={league}
            leagueNumber={leagueNumber}
          />
          <Box mx={2}>
            <p>
              {t(
                "matchesH2h.explain1",
                "Matches are currently being generated, please check back later.",
              )}
            </p>
          </Box>
        </Main>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Main>
        <StandingsHeading
          type="h2h"
          league={league}
          leagueNumber={leagueNumber}
        />
        <Box mx={2}>
          <>
            {now && (
              <H2HNav
                eventId={now.id}
                leagueId={leagueNumber}
                defaultEntry={defaultEntryNumber && defaultEntryNumber}
              />
            )}
            {leagueEntries && (
              <SelectWrap>
                <H2HEntryFilter
                  entryId={entryNumber}
                  leagueEntries={leagueEntries}
                  handleFilterChange={handleEntryFilterChange}
                />
                <H2HEventFilter
                  eventId={eventNumber}
                  startEvent={league.start_event}
                  handleFilterChange={handleEventFilterChange}
                />
              </SelectWrap>
            )}
            {isLargeLeague && (
              <Box m={2}>
                <Alert type="info">
                  {t(
                    "matchesH2h.explain2",
                    "In head-to-head leagues with more than 512 teams only matches for a single team can be viewed.",
                  )}
                </Alert>
              </Box>
            )}
            {now && (
              <MatchesTable
                selectedEntry={entryNumber}
                isAllMine={entryNumber === player?.entry && eventNumber === 0}
                matches={matches.results}
              />
            )}
            <Box mt={4}>
              <Pager>
                <PagerItem>
                  {pageMatches > 1 && (
                    <ButtonLink
                      to={buildMatchesUrl(
                        leagueNumber,
                        eventNumber,
                        entryNumber,
                        pageMatches - 1,
                      )}
                      variant="primary"
                    >
                      <span>{t("matchesH2h.previews", "Previous")}</span>
                    </ButtonLink>
                  )}
                </PagerItem>
                <PagerItemNext>
                  {matches.has_next && (
                    <ButtonLink
                      to={buildMatchesUrl(
                        leagueNumber,
                        eventNumber,
                        entryNumber,
                        pageMatches + 1,
                      )}
                      variant="primary"
                    >
                      <span>{t("matchesH2h.next", "Next")}</span>
                    </ButtonLink>
                  )}
                </PagerItemNext>
              </Pager>
            </Box>
          </>
        </Box>
      </Main>
      <Secondary>
        <></>
      </Secondary>
    </Wrapper>
  );
};

export default withTranslation()(MatchesH2H);
